/**
 * propertyPicker — PURE selection logic for the M5-S4 PropertyPicker component.
 * SDK-free + JSX-free so it is directly unit-testable (node --test / loadTs).
 */

export interface PropertyOption {
  propertyId: string;
  /** address || propertyId — what the staff member reads on the card. */
  label: string;
  role?: string;
}

/** What the picker should do for a given set of options. */
export type PickerDecision =
  | { kind: 'auto'; propertyId: string }
  | { kind: 'none' }
  | { kind: 'choose' };

/**
 * 0 options → 'none' (no property scope); 1 → 'auto' (nothing to choose);
 * >1 → 'choose' (show the chooser).
 */
export function pickerDecision(options: PropertyOption[]): PickerDecision {
  if (options.length === 0) return { kind: 'none' };
  if (options.length === 1) return { kind: 'auto', propertyId: options[0].propertyId };
  return { kind: 'choose' };
}

// ─── AD-036: global searchable selector (client's own surfaced first) ──────

/** A property row in the unified Property step's searchable list. */
export interface PropertyListItem {
  propertyId: string;
  /** Address || propertyId — the headline shown on the card. */
  label: string;
  /** City (sub-line) — may be empty. */
  city?: string;
  /** Whether this property already belongs to the active client (surface first). */
  ownedByClient: boolean;
  /** Whether this property is archived (badge it). */
  archived?: boolean;
}

/**
 * PURE — order properties for the picker: the active client's own properties
 * first (owner ruling: global search, client's own surfaced first), then the
 * rest. Within each group, archived properties sink below active ones; ties
 * keep input order (stable). Returns a NEW array.
 */
export function orderForPicker(items: PropertyListItem[]): PropertyListItem[] {
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => {
      // 1. client-owned first
      if (a.it.ownedByClient !== b.it.ownedByClient) {
        return a.it.ownedByClient ? -1 : 1;
      }
      // 2. active before archived
      const aArch = a.it.archived === true;
      const bArch = b.it.archived === true;
      if (aArch !== bArch) return aArch ? 1 : -1;
      // 3. stable
      return a.i - b.i;
    })
    .map((x) => x.it);
}

/**
 * PURE — case-insensitive substring filter over a property's label + city +
 * propertyId. An empty / whitespace query returns the input unchanged.
 */
export function filterProperties(
  items: PropertyListItem[],
  query: string,
): PropertyListItem[] {
  const q = (query ?? '').trim().toLowerCase();
  if (!q) return items;
  return items.filter((it) => {
    const hay = `${it.label} ${it.city ?? ''} ${it.propertyId}`.toLowerCase();
    return hay.includes(q);
  });
}
